import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Flex, Text, Button, Badge, HStack, VStack, IconButton, Spinner,
  useToast, Divider, Collapse, Tooltip, Table, Thead, Tbody, Tr, Th, Td,
  Alert, AlertIcon, Modal, ModalOverlay, ModalContent, ModalHeader,
  ModalBody, ModalFooter, ModalCloseButton, useDisclosure,
} from '@chakra-ui/react';
import { FiTrash2, FiRefreshCw, FiChevronDown, FiChevronUp, FiAlertTriangle } from 'react-icons/fi';
import api from '../api';

const METHOD_LABELS = { kiosk_form: '📋 Kiosk', web_form: '🌐 Web Form', manual: '👤 Manual' };
const METHOD_COLORS = { kiosk_form: 'orange', web_form: 'blue', manual: 'gray' };

export default function LeadSpamPanel() {
  const [spamList, setSpamList]     = useState([]);
  const [loading, setLoading]       = useState(false);
  const [expanded, setExpanded]     = useState({});
  const [actionItem, setActionItem] = useState(null);
  const [actionType, setActionType] = useState(null); // 'delete' | 'restore'
  const confirmDisc = useDisclosure();
  const toast = useToast();

  const fetchSpam = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/prospects/spam');
      setSpamList(data || []);
    } catch(e) { toast({ title: 'Failed to load spam list', status: 'error', duration: 2000 }); }
    setLoading(false);
  }, [toast]);

  useEffect(() => { fetchSpam(); }, [fetchSpam]);

  const openConfirm = (item, type) => {
    setActionItem(item);
    setActionType(type);
    confirmDisc.onOpen();
  };

  const handleConfirm = async () => {
    confirmDisc.onClose();
    try {
      if (actionType === 'delete') {
        await api.delete(`/prospects/spam/${actionItem.id}`);
        toast({ title: 'Spam record permanently deleted', status: 'success', duration: 2000 });
      } else {
        await api.post(`/prospects/spam/${actionItem.id}/restore`);
        toast({ title: `${actionItem.name} restored to main system`, status: 'success', duration: 2500 });
      }
      fetchSpam();
    } catch(e) {
      toast({ title: 'Action failed', description: e.response?.data?.error || e.message, status: 'error', duration: 3000 });
    }
  };

  const toggle = (id) => setExpanded(p => ({ ...p, [id]: !p[id] }));

  const fmtDate = (d) => d ? new Date(d).toLocaleString('en-GB', { day:'2-digit', month:'short', year:'numeric', hour:'2-digit', minute:'2-digit' }) : '—';

  return (
    <Box>
      <Flex justify="space-between" align="center" mb={4}>
        <VStack align="flex-start" spacing={0}>
          <Text fontWeight="700" fontSize="15px">Lead Spam</Text>
          <Text fontSize="12px" color="gray.400">
            Companies, contacts and communications moved to spam from Prospecting Campaigns.
            Admins can permanently delete or restore entries to the main system.
          </Text>
        </VStack>
        <IconButton icon={<FiRefreshCw size={13} />} size="sm" variant="ghost" onClick={fetchSpam} />
      </Flex>

      {loading && <Flex justify="center" py={8}><Spinner color="red.400" /></Flex>}

      {!loading && spamList.length === 0 && (
        <Box textAlign="center" py={10} bg="gray.50" borderRadius="lg" border="1px" borderColor="gray.200">
          <Text fontSize="32px" mb={2}>🚫</Text>
          <Text fontSize="14px" color="gray.500" fontWeight="600">No spam records</Text>
          <Text fontSize="12px" color="gray.400" mt={1}>
            Leads marked as spam from Prospecting Campaigns will appear here.
          </Text>
        </Box>
      )}

      <VStack spacing={3} align="stretch">
        {spamList.map(item => (
          <Box key={item.id} border="1px" borderColor="red.200" borderRadius="lg" bg="white" overflow="hidden">
            {/* Header row */}
            <Flex px={4} py={3} bg="red.50" align="center" justify="space-between"
              borderBottom="1px" borderColor="red.100" cursor="pointer" onClick={() => toggle(item.id)}>
              <HStack spacing={3}>
                <Text fontSize="14px" fontWeight="700" color="red.800">{item.name || '(no company)'}</Text>
                <Badge colorScheme={METHOD_COLORS[item.source_type] || 'gray'} fontSize="9px">
                  {METHOD_LABELS[item.source_type] || item.source_type}
                </Badge>
                {item.source_form_title && (
                  <Text fontSize="11px" color="gray.500" fontStyle="italic">via "{item.source_form_title}"</Text>
                )}
              </HStack>
              <HStack spacing={2} onClick={e => e.stopPropagation()}>
                <Text fontSize="11px" color="gray.400">{fmtDate(item.spammed_at)}</Text>
                <Tooltip label="Restore to main system">
                  <IconButton icon={<FiRefreshCw size={13} />} size="xs" colorScheme="green" variant="outline"
                    onClick={() => openConfirm(item, 'restore')} />
                </Tooltip>
                <Tooltip label="Permanently delete">
                  <IconButton icon={<FiTrash2 size={13} />} size="xs" colorScheme="red" variant="outline"
                    onClick={() => openConfirm(item, 'delete')} />
                </Tooltip>
                <IconButton icon={expanded[item.id] ? <FiChevronUp size={13} /> : <FiChevronDown size={13} />}
                  size="xs" variant="ghost" onClick={() => toggle(item.id)} />
              </HStack>
            </Flex>

            {/* Expanded detail */}
            <Collapse in={!!expanded[item.id]}>
              <Box px={4} py={3}>
                <Box display="grid" gridTemplateColumns="1fr 1fr 1fr" gap={4}>
                  {/* Contacts */}
                  <Box>
                    <Text fontSize="11px" fontWeight="700" color="gray.600" mb={2} textTransform="uppercase" letterSpacing="0.05em">
                      Contacts ({(item.contacts || []).length})
                    </Text>
                    {(item.contacts || []).length === 0 ? (
                      <Text fontSize="11px" color="gray.400">None</Text>
                    ) : (
                      <VStack align="stretch" spacing={1}>
                        {(item.contacts || []).map((ct, i) => (
                          <Box key={i} bg="gray.50" borderRadius="md" p={2}>
                            <Text fontSize="12px" fontWeight="600">{[ct.first_name, ct.last_name].filter(Boolean).join(' ') || '(unnamed)'}</Text>
                            {ct.email && <Text fontSize="11px" color="blue.600">{ct.email}</Text>}
                            {ct.phone && <Text fontSize="11px" color="gray.500">{ct.phone}</Text>}
                          </Box>
                        ))}
                      </VStack>
                    )}
                  </Box>

                  {/* Communications */}
                  <Box gridColumn="span 2">
                    <Text fontSize="11px" fontWeight="700" color="gray.600" mb={2} textTransform="uppercase" letterSpacing="0.05em">
                      Communications ({(item.communications || []).length})
                    </Text>
                    {(item.communications || []).length === 0 ? (
                      <Text fontSize="11px" color="gray.400">None</Text>
                    ) : (
                      <VStack align="stretch" spacing={1} maxH="160px" overflowY="auto">
                        {(item.communications || []).map((cm, i) => (
                          <Box key={i} bg="gray.50" borderRadius="md" p={2} border="1px" borderColor="gray.200">
                            <Flex justify="space-between" mb={0.5}>
                              <HStack spacing={1}>
                                <Badge colorScheme={METHOD_COLORS[cm.method] || 'gray'} fontSize="8px">
                                  {METHOD_LABELS[cm.method] || cm.method}
                                </Badge>
                                <Badge colorScheme={cm.direction === 'inbound' ? 'green' : 'purple'} fontSize="8px">
                                  {cm.direction}
                                </Badge>
                              </HStack>
                              <Text fontSize="10px" color="gray.400">{cm.comm_date || '—'}</Text>
                            </Flex>
                            {cm.subject && <Text fontSize="11px" fontWeight="600" color="gray.700">{cm.subject}</Text>}
                            {cm.body && (
                              <Text fontSize="10px" color="gray.500" noOfLines={3} mt={0.5}>{cm.body}</Text>
                            )}
                          </Box>
                        ))}
                      </VStack>
                    )}
                  </Box>
                </Box>
              </Box>
            </Collapse>
          </Box>
        ))}
      </VStack>

      {/* Confirm Modal */}
      <Modal isOpen={confirmDisc.isOpen} onClose={confirmDisc.onClose} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader fontSize="16px">
            {actionType === 'delete' ? '⚠️ Permanently Delete Spam Record' : '♻️ Restore to Main System'}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {actionType === 'delete' ? (
              <VStack align="stretch" spacing={3}>
                <Alert status="error" borderRadius="md">
                  <AlertIcon />
                  <Text fontSize="13px">This action cannot be undone. The company, contacts, and communications will be permanently erased.</Text>
                </Alert>
                <Text fontSize="14px">
                  Permanently delete <strong>{actionItem?.name}</strong> and all associated spam records?
                </Text>
              </VStack>
            ) : (
              <VStack align="stretch" spacing={3}>
                <Alert status="info" borderRadius="md">
                  <AlertIcon />
                  <Text fontSize="13px">The company, contacts, and communications will be recreated in the main system. No prospect entry will be created — you can add them to a campaign manually.</Text>
                </Alert>
                <Text fontSize="14px">
                  Restore <strong>{actionItem?.name}</strong> back to the main companies and contacts?
                </Text>
              </VStack>
            )}
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={confirmDisc.onClose}>Cancel</Button>
            <Button
              colorScheme={actionType === 'delete' ? 'red' : 'green'}
              onClick={handleConfirm}>
              {actionType === 'delete' ? 'Permanently Delete' : 'Restore to System'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}
